import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Workforce, WorkforceStatus, getDaysUntilEnd } from "@/types/workforce";
import { toast } from "@/hooks/use-toast";

const ITEMS_PER_PAGE = 25;

export type WorkforceSortField = "candidateName" | "startDate" | "status" | "dateCreated";
export type WorkforceSortOrder = "asc" | "desc";

export interface WorkforceFilters {
	searchTerm?: string;
	statusFilter?: WorkforceStatus[];
	assignedToMe?: boolean;
	currentUserId?: string | null;
	dateRangeStart?: string;
	dateRangeEnd?: string;
	endingSoon?: number | null; // Filter by assignments ending within X days
}

export interface UseWorkforcePaginatedReturn {
	workforce: Workforce[];
	isLoading: boolean;
	totalCount: number;
	totalPages: number;
	currentPage: number;
	setCurrentPage: (page: number) => void;
	refetch: () => Promise<void>;
}

export const useWorkforcePaginated = (
	filters: WorkforceFilters,
	sortField: WorkforceSortField = "dateCreated",
	sortOrder: WorkforceSortOrder = "desc"
): UseWorkforcePaginatedReturn => {
	const [workforce, setWorkforce] = useState<Workforce[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [totalCount, setTotalCount] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [orgUuid, setOrgUuid] = useState<string | null>(null);

	// Fetch organization UUID once on mount
	useEffect(() => {
		const fetchOrgUuid = async () => {
			try {
				const { data: { user } } = await supabase.auth.getUser();
				if (!user) return;

				const { data: userOrg } = await supabase
					.from('user_organization')
					.select('organization_uuid')
					.eq('user_uuid', user.id)
					.single();

				if (userOrg?.organization_uuid) {
					setOrgUuid(userOrg.organization_uuid);
				}
			} catch (error) {
				console.error("Error fetching organization:", error);
			}
		};

		fetchOrgUuid();
	}, []);

	const fetchWorkforce = useCallback(async () => {
		if (!orgUuid) return;

		try {
			setIsLoading(true);

			// Start building query - exclude bench entries
			let query = supabase
				.from('workforce')
				.select(`
					*,
					candidates!inner(id, name, email, phone, function, skills),
					jobs(id, title, companies(id, name))
				`, { count: 'exact' })
				.eq('organization_uuid', orgUuid)
				.neq('is_bench', true);

			// Status filter
			if (filters.statusFilter && filters.statusFilter.length > 0) {
				query = query.in('status', filters.statusFilter);
			}

			// Assigned to me filter
			if (filters.assignedToMe && filters.currentUserId) {
				query = query.eq('assigned_recruiter', filters.currentUserId);
			}

			// Date range filters
			if (filters.dateRangeStart) {
				query = query.gte('start_date', filters.dateRangeStart);
			}
			if (filters.dateRangeEnd) {
				query = query.lte('start_date', filters.dateRangeEnd);
			}

			// Execute first query to get data
			const { data, error, count } = await query;

			if (error) {
				throw error;
			}

			// Transform data
			let transformedData: Workforce[] = (data || []).map((item: any) => ({
				...item,
				candidate: item.candidates,
				job: item.jobs ? {
					...item.jobs,
					company: item.jobs.companies
				} : undefined
			}));

			// Client-side search (searches candidate name, company name, job title)
			if (filters.searchTerm && filters.searchTerm.trim() !== "") {
				const term = filters.searchTerm.toLowerCase().trim();
				transformedData = transformedData.filter(w => {
					const candidateName = w.candidate?.name?.toLowerCase() || "";
					const companyName = w.job?.company?.name?.toLowerCase() || w.external_company_name?.toLowerCase() || "";
					const jobTitle = w.job?.title?.toLowerCase() || w.external_job_title?.toLowerCase() || "";
					const location = w.location?.toLowerCase() || "";

					return candidateName.includes(term) ||
						   companyName.includes(term) ||
						   jobTitle.includes(term) ||
						   location.includes(term);
				});
			}

			// Ending soon filter (only for active assignments)
			if (filters.endingSoon !== undefined && filters.endingSoon !== null) {
				const threshold = filters.endingSoon;
				transformedData = transformedData.filter(w => {
					if (w.status !== 'active') return false;
					const endDate = w.expected_end_date || w.end_date;
					const daysUntil = getDaysUntilEnd(endDate);
					return daysUntil !== null && daysUntil >= 0 && daysUntil <= threshold;
				});
			}

			// Sorting
			const sortFunctions: Record<WorkforceSortField, (a: Workforce, b: Workforce) => number> = {
				candidateName: (a, b) => (a.candidate?.name || "").localeCompare(b.candidate?.name || ""),
				startDate: (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime(),
				status: (a, b) => a.status.localeCompare(b.status),
				dateCreated: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
			};

			transformedData.sort((a, b) => {
				const result = sortFunctions[sortField](a, b);
				return sortOrder === 'asc' ? result : -result;
			});

			// Get total count after filtering
			const filteredCount = transformedData.length;

			// Pagination - AFTER all filters
			const from = (currentPage - 1) * ITEMS_PER_PAGE;
			const paginatedData = transformedData.slice(from, from + ITEMS_PER_PAGE);

			setWorkforce(paginatedData);
			setTotalCount(filteredCount);
		} catch (error) {
			console.error("Error fetching workforce:", error);
			toast({
				title: "Failed to load workforce",
				description: error instanceof Error ? error.message : "Unknown error",
				variant: "destructive",
			});
			setWorkforce([]);
			setTotalCount(0);
		} finally {
			setIsLoading(false);
		}
	}, [
		orgUuid,
		currentPage,
		filters.searchTerm,
		filters.statusFilter,
		filters.assignedToMe,
		filters.currentUserId,
		filters.dateRangeStart,
		filters.dateRangeEnd,
		filters.endingSoon,
		sortField,
		sortOrder
	]);

	useEffect(() => {
		fetchWorkforce();
	}, [fetchWorkforce]);

	// Reset to page 1 when filters change
	useEffect(() => {
		setCurrentPage(1);
	}, [
		filters.searchTerm,
		filters.statusFilter,
		filters.assignedToMe,
		filters.dateRangeStart,
		filters.dateRangeEnd,
		filters.endingSoon
	]);

	const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

	return {
		workforce,
		isLoading,
		totalCount,
		totalPages,
		currentPage,
		setCurrentPage,
		refetch: fetchWorkforce
	};
};
